import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { BASE_URL, SITE_NAME } from "../../config/constants";
import { removeUser } from "../../redux/slice/authSlice";
import ClientHeader from "./ClientHeader";
import RecentStudents from "./RecentStudents";
import Messages from "./Messages";
import {
  AdminSidebar,
  useIsMobileSidebar,
} from "./AdminSidebar";
import { MobileNavDrawer } from "./MobileNav";

const SWIPE_OPEN_MIN = 56;
const SWIPE_EDGE_Y = 88;

const ClientLayout = ({ children, head }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobileSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === "/";
  const { userToken } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const touchStart = useRef(null);

  let title = head?.title ? `${SITE_NAME} | ${head.title}` : SITE_NAME;

  useLayoutEffect(() => {
    if (!userToken) {
      navigate("/signin", { replace: true });
      return;
    }

    const decodedToken = jwtDecode(userToken);
    if (decodedToken.exp < Date.now() / 1000) {
      dispatch(removeUser());
      navigate("/signin", { replace: true });
    }
  }, [userToken, dispatch, navigate]);

  const checkServerStatus = async () => {
    try {
      const response = await fetch(BASE_URL + "/health");
      if (!response.ok) {
        dispatch(removeUser());
        navigate("/signin", { replace: true });
      }
    } catch (error) {
      dispatch(removeUser());
      navigate("/signin", { replace: true });
    }
  };

  useLayoutEffect(() => {
    checkServerStatus();
    const id = setInterval(checkServerStatus, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) return undefined;

    const onTouchStart = (event) => {
      if (menuOpen) return;
      const touch = event.touches?.[0];
      if (!touch) return;
      if (touch.clientY > SWIPE_EDGE_Y) {
        touchStart.current = null;
        return;
      }
      touchStart.current = { x: touch.clientX, y: touch.clientY };
    };

    const onTouchEnd = (event) => {
      if (menuOpen || !touchStart.current) return;
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      const dy = touch.clientY - touchStart.current.y;
      const dx = Math.abs(touch.clientX - touchStart.current.x);
      touchStart.current = null;
      if (dy >= SWIPE_OPEN_MIN && dy > dx * 1.2) {
        setMenuOpen(true);
      }
    };

    const onTouchCancel = () => {
      touchStart.current = null;
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchCancel, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchCancel);
    };
  }, [isMobile, menuOpen]);

  return (
    <div className={`admin-shell${isMobile ? " is-mobile" : ""}`}>
      {!isMobile && <AdminSidebar />}
      {isMobile && (
        <MobileNavDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
      )}

      <div className={`admin-main${isDashboard ? " has-side-cards" : ""}`}>
        <ClientHeader
          title={head?.title || "Dashboard"}
          isMobile={isMobile}
          menuOpen={menuOpen}
          onMenuToggle={() => setMenuOpen((open) => !open)}
        />
        <div className="admin-body">
          <main className="admin-content">{children}</main>
          {isDashboard && (
            <aside className="admin-right">
              <RecentStudents />
              <Messages />
            </aside>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientLayout;
